import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Gift, MessageSquare, CalendarClock, TrendingUp, ArrowRight,
  DollarSign, GitBranch, UserPlus, Percent, Package, Megaphone, FlaskConical,
} from "lucide-react";
import { api } from "@/lib/api";
import { ScoreBadge, StageBadge, PlatformTag, Spinner } from "@/components/app/widgets";

const sectionCls = "border border-[#27272A] bg-[#0A0A0A] p-6";
const headCls = "flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-300";
const numCls = "font-serif text-lg italic text-[#D4AF37]";

export default function Overview() {
  const [cc, setCc] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [val, setVal] = useState(null);

  useEffect(() => {
    api.get("/dashboard/command-center").then((r) => setCc(r.data)).catch(() => setCc({}));
    api.get("/dashboard/overview").then((r) => setKpi(r.data)).catch(() => setKpi({}));
    api.get("/dashboard/validation").then((r) => setVal(r.data)).catch(() => setVal({}));
  }, []);

  if (!cc || !kpi || !val) return <Spinner label="Loading your command center" />;

  const KPIS = [
    { label: "Products", value: kpi.products, icon: Package },
    { label: "Campaigns", value: kpi.active_campaigns, icon: Megaphone },
    { label: "Prospects", value: kpi.prospects, icon: Users },
    { label: "Partnerships", value: kpi.partnerships, icon: GitBranch },
    { label: "Leads", value: kpi.leads, icon: UserPlus },
    { label: "Revenue", value: `$${Number(kpi.revenue || 0).toLocaleString()}`, icon: DollarSign },
    { label: "Conversion", value: `${kpi.conversion_rate || 0}%`, icon: Percent },
    { label: "Follow-ups today", value: kpi.follow_ups_today || 0, icon: CalendarClock },
  ];

  return (
    <div data-testid="command-center">
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-semibold tracking-tight">Distribution Command Center</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Build the machine → use the machine → measure the machine → improve the machine.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
        {KPIS.map((k, i) => (
          <motion.div
            key={k.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: i * 0.04 }}
            data-testid={`kpi-${k.label.toLowerCase().replace(/\W+/g, "-")}`}
            className="border border-[#27272A] bg-[#0A0A0A] p-4"
          >
            <k.icon className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.5} />
            <p className="mt-2 font-serif text-2xl font-semibold">{k.value}</p>
            <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-zinc-500">{k.label}</p>
          </motion.div>
        ))}
      </div>

      {/* REAL-WORLD VALIDATION — real, user-confirmed outcomes only */}
      <section className={`${sectionCls} mt-6`} data-testid="cc-validation">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={headCls}><FlaskConical className="h-4 w-4 text-[#D4AF37]" /> Real-World Validation</h2>
          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
            Real outcomes only — demo data excluded, responses confirmed by you
          </p>
        </div>
        {!val.messages_sent ? (
          <p className="mt-4 text-sm text-zinc-500" data-testid="validation-empty">
            No real outreach sent yet. Approve a draft, send it yourself, then record what actually happened
            on the prospect page — this panel counts only real, confirmed outcomes.
          </p>
        ) : (
          <>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {[
                { label: "Prospects contacted", value: val.prospects_contacted, tid: "val-contacted" },
                { label: "Messages sent", value: val.messages_sent, tid: "val-sent" },
                { label: "Responses", value: `${val.responses}${val.response_rate != null ? ` · ${val.response_rate}%` : ""}`, tid: "val-responses" },
                { label: "Positive responses", value: `${val.positive_responses}${val.positive_response_rate != null ? ` · ${val.positive_response_rate}%` : ""}`, tid: "val-positive" },
                { label: "Partnerships", value: `${val.partnerships}${val.partnership_rate != null ? ` · ${val.partnership_rate}%` : ""}`, tid: "val-partnerships" },
                { label: "Revenue", value: `$${Number(val.revenue || 0).toLocaleString()}`, tid: "val-revenue" },
              ].map((m) => (
                <div key={m.tid} data-testid={m.tid} className="border border-white/10 p-4">
                  <p className="font-serif text-xl font-semibold text-white">{m.value}</p>
                  <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.14em] text-zinc-500">{m.label}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-500">
              <span data-testid="val-no-response">{val.no_responses || 0} recorded as no response (not counted as a response)</span>
              <span data-testid="val-awaiting">{val.awaiting_classification || 0} sent awaiting your classification</span>
              <span data-testid="val-leads">{val.leads} leads · {val.customers} customers</span>
              {val.revenue_per_partnership != null && (
                <span data-testid="val-rev-per-partnership">${val.revenue_per_partnership.toLocaleString()} revenue per partnership</span>
              )}
            </div>
            {(val.best_platform || val.best_product || val.best_partnership_format || val.best_value_offer) && (
              <div className="mt-4 grid gap-3 border-t border-white/10 pt-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="val-best">
                {[
                  ["Best platform", val.best_platform],
                  ["Best product", val.best_product],
                  ["Best partnership format", val.best_partnership_format],
                  ["Best value-first offer", val.best_value_offer],
                ].map(([label, b]) => b && (
                  <div key={label}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</p>
                    <p className="mt-1 truncate text-sm font-medium text-[#D4AF37]">{b.name}</p>
                    <p className="text-[11px] text-zinc-500">{b.leads} leads · {b.customers} customers · ${b.revenue.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Q1 — WHO SHOULD I CONTACT? */}
        <section className={sectionCls} data-testid="cc-contact">
          <h2 className={headCls}><Users className="h-4 w-4 text-[#D4AF37]" /> 1 · Who should I contact?</h2>
          {cc.contact_next?.length ? (
            <ul className="mt-4 divide-y divide-white/5">
              {cc.contact_next.map((p, i) => (
                <li key={p.prospect_id}>
                  <Link to={`/app/prospects/${p.prospect_id}`} data-testid={`cc-contact-${p.prospect_id}`} className="flex items-center gap-3 py-3 transition-colors hover:bg-white/[0.03]">
                    <span className={numCls}>{i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-white">{p.name}</span>
                      {p.next_action?.action && <span className="block truncate text-[11px] text-zinc-500">{p.next_action.action}</span>}
                    </span>
                    <PlatformTag platform={p.platform} />
                    {p.match?.match_score != null && (
                      <span className="text-xs font-bold text-[#D4AF37]">M{p.match.match_score}</span>
                    )}
                    <ScoreBadge score={p.score?.total} />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-zinc-500">No contact-ready prospects. Run discovery and Analyze Campaign on a campaign first.</p>
          )}
        </section>

        {/* Q2 — WHAT VALUE FIRST? */}
        <section className={sectionCls} data-testid="cc-value">
          <h2 className={headCls}><Gift className="h-4 w-4 text-[#D4AF37]" /> 2 · What value should I provide first?</h2>
          {cc.value_first?.length ? (
            <ul className="mt-4 space-y-3">
              {cc.value_first.map((v) => (
                <li key={v.prospect_id} className="border border-white/10 p-4">
                  <Link to={`/app/prospects/${v.prospect_id}`} className="text-sm font-medium text-white hover:text-[#D4AF37]">{v.name}</Link>
                  <p className="mt-1 text-sm text-[#D4AF37]">{v.offer}</p>
                  {v.why && <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-zinc-500">{v.why}</p>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-zinc-500">Run the Value Strategy agent on your top prospects to see what to give before you ask.</p>
          )}
        </section>

        {/* Q3 — WHAT SHOULD I SAY? */}
        <section className={sectionCls} data-testid="cc-say">
          <h2 className={headCls}><MessageSquare className="h-4 w-4 text-[#D4AF37]" /> 3 · What should I say?</h2>
          <div className="mt-4 space-y-3">
            {cc.approved_to_send?.length > 0 && (
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                Approved — ready to send ({cc.approved_to_send.length})
              </p>
            )}
            {(cc.approved_to_send || []).map((m) => (
              <Link key={m.message_id} to={`/app/prospects/${m.prospect_id}`} data-testid={`cc-approved-${m.message_id}`} className="block border border-emerald-500/30 bg-emerald-500/5 p-4 transition-colors hover:border-emerald-500/60">
                <p className="text-sm font-medium text-white">{m.prospect_name}</p>
                <p className="mt-0.5 text-xs text-zinc-400">{m.type.replace(/_/g, " ")}{m.subject ? ` — ${m.subject}` : ""}</p>
              </Link>
            ))}
            {cc.drafts_to_review?.length > 0 && (
              <p className="pt-2 text-[10px] font-bold uppercase tracking-wider text-amber-400">
                Awaiting your approval ({cc.drafts_to_review.length})
              </p>
            )}
            {(cc.drafts_to_review || []).map((m) => (
              <Link key={m.message_id} to={`/app/prospects/${m.prospect_id}`} data-testid={`cc-draft-${m.message_id}`} className="block border border-white/10 p-4 transition-colors hover:border-[#D4AF37]/40">
                <p className="text-sm font-medium text-white">{m.prospect_name}</p>
                <p className="mt-0.5 text-xs text-zinc-400">{m.type.replace(/_/g, " ")}{m.subject ? ` — ${m.subject}` : ""}</p>
              </Link>
            ))}
            {!cc.drafts_to_review?.length && !cc.approved_to_send?.length && (
              <p className="text-sm text-zinc-500">No drafts in the queue. Generate outreach from any prospect page.</p>
            )}
          </div>
        </section>

        {/* Q4 — WHO TO FOLLOW UP? */}
        <section className={sectionCls} data-testid="cc-followup">
          <h2 className={headCls}><CalendarClock className="h-4 w-4 text-[#D4AF37]" /> 4 · Who should I follow up with?</h2>
          {cc.follow_ups_due?.length || cc.waiting_for_response?.length ? (
            <ul className="mt-4 divide-y divide-white/5">
              {(cc.follow_ups_due || []).map((p) => (
                <li key={p.prospect_id}>
                  <Link to={`/app/prospects/${p.prospect_id}`} data-testid={`cc-fu-${p.prospect_id}`} className="flex items-center gap-3 py-3 hover:bg-white/[0.03]">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">{p.name}</span>
                    <span className="text-[11px] font-semibold text-amber-400">due {p.follow_up?.due_date}</span>
                    <StageBadge stage={p.status} />
                  </Link>
                </li>
              ))}
              {(cc.waiting_for_response || []).map((p) => (
                <li key={p.prospect_id}>
                  <Link to={`/app/prospects/${p.prospect_id}`} data-testid={`cc-wait-${p.prospect_id}`} className="flex items-center gap-3 py-3 hover:bg-white/[0.03]">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">{p.name}</span>
                    <span className="text-[11px] text-zinc-500">waiting for response</span>
                    <StageBadge stage={p.status} />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-zinc-500">Nothing due. Schedule follow-ups from prospect pages or ask the Follow-Up Manager.</p>
          )}
          <Link to="/app/follow-ups" className="mt-4 flex items-center gap-1 text-xs text-[#D4AF37] hover:brightness-110">
            Open Follow-Up Manager <ArrowRight className="h-3 w-3" />
          </Link>
        </section>
      </div>

      {/* Q5 — WHAT IS GENERATING RESULTS? */}
      <section className={`${sectionCls} mt-6`} data-testid="cc-results">
        <h2 className={headCls}><TrendingUp className="h-4 w-4 text-[#D4AF37]" /> 5 · What is actually generating partnerships, leads and revenue?</h2>
        <div className="mt-4 grid gap-6 lg:grid-cols-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Top partners (real revenue)</p>
            {cc.results?.top_partners?.length ? (
              <ul className="mt-3 space-y-2">
                {cc.results.top_partners.map((t) => (
                  <li key={t.prospect_id} className="flex items-center gap-3 text-sm">
                    <Link to={`/app/prospects/${t.prospect_id}`} className="min-w-0 flex-1 truncate text-white hover:text-[#D4AF37]">{t.name}</Link>
                    <span className="text-xs text-zinc-500">{t.customers} cust.</span>
                    <span className="font-semibold text-[#D4AF37]">${t.revenue.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-zinc-500">No real revenue yet — demo data is excluded by design. Convert a lead to see this come alive.</p>
            )}
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">By platform (real)</p>
            {Object.keys(cc.results?.by_platform || {}).length ? (
              <ul className="mt-3 space-y-2">
                {Object.entries(cc.results.by_platform).map(([pl, s]) => (
                  <li key={pl} className="flex items-center gap-3 text-sm">
                    <PlatformTag platform={pl} />
                    <span className="flex-1 text-xs text-zinc-500">{s.leads} leads · {s.customers} customers</span>
                    <span className="font-semibold text-[#D4AF37]">${s.revenue.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-zinc-500">No platform revenue yet.</p>
            )}
            <Link to="/app/analytics" className="mt-4 flex items-center gap-1 text-xs text-[#D4AF37] hover:brightness-110">
              Learning Insights <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Portfolio (per product)</p>
            <ul className="mt-3 space-y-2">
              {(cc.portfolio || []).map((p) => (
                <li key={p.product_id}>
                  <Link to={`/app/products/${p.product_id}`} data-testid={`cc-product-${p.product_id}`} className="block border border-white/10 p-3 transition-colors hover:border-[#D4AF37]/40">
                    <p className="truncate text-sm font-medium text-white">{p.name}</p>
                    <p className="mt-0.5 text-[11px] text-zinc-500">
                      {p.prospects} prospects · {p.partnerships} partners · {p.leads} leads ·{" "}
                      <span className="text-[#D4AF37]">${p.revenue.toLocaleString()}</span>
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
